import React, { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";

import { MediumHeader, SemibigHeader } from "@narvar/nth-bit-header";
import Button from "@narvar/nth-block-button";
import { useSettings, withSettingsProvider } from "@narvar/nth-hook-settings";
import { useTheme, withThemeProvider } from "@narvar/nth-hook-theme";
import {
  useTranslations,
  withTranslationsProvider,
} from "@narvar/nth-hook-translations";
import {
  OrderItem,
  SubmittedReturn,
  useLanguage,
  useReturnsConfig,
} from "@narvar/nth-kit-returns-headless";
import {
  ExtendedOrderItem,
  ExtendedReturnItemInput,
} from "../ReturnReasons/types";
import {
  Footer,
  ItemCard,
  Language,
  LineItemsGroup,
  Spinner,
  StickyFooter,
  StyledMarkup,
  TextLink,
} from "../ReviewSummary/components";
import { useFeature } from "../ReviewSummary/utils/features";
import { getItemAttrs, useItemCardLabels } from "./helper";
import { useStyles } from "./ItemSelectorRawStyles";
import { themeTokens, translationKeys } from "./tokens";
import {
  ItemSelectorRawProps,
  LineItemGrouping,
  ReturnShipmentAction,
} from "./types";
import { EMPTY_ARRAY } from "../../shared/modules/object";

const ItemSelectorRawC = ({
  title,
  customer,
  lineItemGroupings,
  itemGroupingModeEnabled,
  eligibleItems,
  onClick,
  onSubmit,
  submittedReturns = [],
  ineligibleItems,
  selectedItems,
  features = {},
  onCancel,
  cancelReturnStatus,
  progressiveStep,
  activeStep,
  banner,
  disclaimer,
  locale,
  includeCurrency,
}: ItemSelectorRawProps) => {
  const styles = useStyles();
  const [selectedLineItemGroup, setSelectedLineItemGroup] = React.useState<
    string | undefined
  >();
  const [lineItemGroupReordered, setLineItemGroupReordered] =
    React.useState(lineItemGroupings);
  const { itemDisplay } = useSettings();
  const {
    returnsItemListSubmit,
    returnsItemListIneligibleTitle,
    returnsItemListTitleDirty,
    returnsItemListTitleDirtyHipaa,
    returnsItemListTitleHipaa,
    returnsItemListTitle,
    returnsActionsBackToLogin,
    returnsItemListReturnInitiatedTitle,
    returnsItemListIneligibleDescription,
  } = useTranslations();
  const { skuLabel, qtyLabel, colorLabel, sizeLabel, reasonLabel } =
    useItemCardLabels();

  const { rawValue: rawSubmitLabel } = useLanguage({
    langKey: "returnsItemListSubmit",
  });
  const submitLabel = returnsItemListSubmit ?? rawSubmitLabel;

  const groupItemsMap = useMemo(
    () =>
      (lineItemGroupReordered ?? []).reduce(
        (p, c, i) => ({
          ...p,
          [c.slug ?? `group-${i}`]: eligibleItems.filter((item) =>
            c.lineItemIds?.includes(item.itemId || ""),
          ),
        }),
        {} as Record<string, ExtendedOrderItem[]>,
      ),
    [lineItemGroupReordered, eligibleItems],
  );

  // if shopify collective is enabled, we want to group return-eligible items by
  // fulfillment service and only allow same service items to be selected. also
  // want any previously selected items at the top of the queue.
  React.useEffect(() => {
    if (
      !lineItemGroupings?.length ||
      !selectedItems?.length ||
      !itemGroupingModeEnabled
    )
      return;
    if (selectedItems?.length > 0) {
      const selectedGroupId = lineItemGroupings.find((iGroup) =>
        iGroup.lineItemIds?.find(
          (lineItemId: string) => lineItemId === selectedItems[0].itemId,
        ),
      )?.slug;
      setSelectedLineItemGroup(selectedGroupId);

      const newLineItemsOrder = lineItemGroupings.reduce(
        (newOrdering: LineItemGrouping[], currentGroup) => {
          const isSelectedGroup = currentGroup.lineItemIds?.find(
            (itemId) => itemId === selectedItems[0].itemId,
          );
          if (isSelectedGroup) {
            const reorderedLineItems = currentGroup.lineItemIds?.reduce(
              (newOrder: string[], currentId) => {
                if (selectedItems.find((item) => item.itemId === currentId)) {
                  newOrder.unshift(currentId);
                } else newOrder.push(currentId);
                return newOrder;
              },
              [],
            );

            newOrdering.unshift({
              ...currentGroup,
              lineItemIds: reorderedLineItems,
            });
          } else newOrdering.push(currentGroup);
          return newOrdering;
        },
        [],
      );

      setLineItemGroupReordered(newLineItemsOrder);
    }
  }, [selectedItems, lineItemGroupings, itemGroupingModeEnabled]);

  const handleClick = useCallback(
    (item: OrderItem) => {
      onClick?.({
        value: { item },
        event: "click",
        area: "itemPicker",
        subComponent: "itemPickerList",
      });
    },
    [onClick],
  );

  const handleSubmit = () => {
    onSubmit?.({
      value: {},
      event: "submit",
      area: "itemPicker",
      subComponent: "itemPickerList",
    });
  };

  const handleCancel = (submittedReturn: SubmittedReturn) => {
    onCancel?.({
      value: { submittedReturn },
      event: "click",
      area: "itemPicker",
      subComponent: "submittedReturn",
    });
  };

  const handleReturnToLogin = () => {
    onClick?.({
      value: {},
      event: "click",
      area: "itemPicker",
      subComponent: "returnToLogin",
    });
  };

  const { isHipaaCompliant } = useReturnsConfig();

  const {
    panelcontainerNoneNone: sDefault,
    linksNoneDefault,
    linksNoneHover,
    bodytextDefaultNone,
    documentbodyNoneNone,
  } = useTheme();

  const { returnToLogin } = useFeature(features, "returnToLogin");

  const noVisible = [
    eligibleItems.length === 0,
    ineligibleItems.length === 0,
    submittedReturns.length === 0,
  ].every((e) => e);

  const getReturnsItemListTitle = ({
    eligibleItems,
    selectedItems,
    isHipaaCompliant = false,
  }: {
    eligibleItems: ExtendedOrderItem[];
    selectedItems?: ExtendedReturnItemInput[];
    isHipaaCompliant?: boolean;
  }) => {
    if (eligibleItems.length === 0) {
      return {
        defaultString:
          typeof returnsItemListIneligibleTitle === "string"
            ? returnsItemListIneligibleTitle
            : undefined,
        langKey: "returnsItemListIneligibleTitle",
      };
    }

    if ((selectedItems?.length ?? 0) > 0) {
      return isHipaaCompliant
        ? {
            defaultString:
              typeof returnsItemListTitleDirtyHipaa === "string"
                ? returnsItemListTitleDirtyHipaa
                : undefined,
            langKey: "returnsItemListTitleDirtyHipaa",
          }
        : {
            defaultString:
              typeof returnsItemListTitleDirty === "string"
                ? returnsItemListTitleDirty
                : undefined,
            langKey: "returnsItemListTitleDirty",
          };
    }

    return isHipaaCompliant
      ? {
          defaultString:
            typeof returnsItemListTitleHipaa === "string"
              ? returnsItemListTitleHipaa
              : undefined,
          langKey: "returnsItemListTitleHipaa",
        }
      : {
          defaultString:
            typeof returnsItemListTitle === "string"
              ? returnsItemListTitle
              : undefined,
          langKey: "returnsItemListTitle",
        };
  };

  const itemListTitleObj = getReturnsItemListTitle({
    eligibleItems,
    selectedItems,
    isHipaaCompliant,
  });

  return (
    <>
      <div style={{ width: "100%" }}>
        {banner}
        <SemibigHeader
          containerCustomStyle={{
            margin: "2rem auto",
          }}>
          {title ? (
            <StyledMarkup content={title} />
          ) : (
            <Language
              noWrap
              langKey={itemListTitleObj.langKey}
              defaultString={itemListTitleObj.defaultString}
              replacementTokens={[
                {
                  token: "{{customer}}",
                  value:
                    customer?.firstName ??
                    customer?.lastName ??
                    customer?.fullName ??
                    "",
                },
              ]}
            />
          )}
        </SemibigHeader>
        {noVisible && returnToLogin && (
          <Button onClick={handleReturnToLogin}>
            <Language
              langKey="returnsActionsBackToLogin"
              defaultString={returnsActionsBackToLogin}
            />
          </Button>
        )}
        {itemGroupingModeEnabled && lineItemGroupReordered?.length ? (
          lineItemGroupReordered.map((iGroup, i) => (
            <LineItemsGroup
              key={iGroup.slug ?? `group-${i}`}
              group={iGroup}
              selectedGroup={iGroup.slug === selectedLineItemGroup}
              itemsWereSelected={Boolean(selectedItems?.length)}
              items={groupItemsMap[iGroup.slug ?? `group-${i}`] ?? EMPTY_ARRAY}
              selectedItems={selectedItems}
              itemDisplay={itemDisplay}
              locale={locale}
              includeCurrency={includeCurrency}
              onItemClick={handleClick}
            />
          ))
        ) : (
          <LineItemsGroup
            items={eligibleItems}
            selectedItems={selectedItems}
            itemDisplay={itemDisplay}
            locale={locale}
            includeCurrency={includeCurrency}
            onItemClick={handleClick}
          />
        )}
      </div>
      {submittedReturns?.map((submittedReturn) => {
        const isBeingCancelled =
          cancelReturnStatus?.status === "loading" &&
          cancelReturnStatus?.returnId === submittedReturn.id;

        const showReturnLabelActions = !isHipaaCompliant;
        const showShipmentActions =
          showReturnLabelActions || submittedReturn.canBeCancelled;

        return (
          <React.Fragment key={submittedReturn.id}>
            {submittedReturn.returnShipments?.map((shipment, i) => {
              const actions: ReturnShipmentAction[] =
                shipment.returnShipmentActions ?? [];

              if (!shipment.returnShipmentActions) {
                if (showReturnLabelActions) {
                  shipment.returnLabels.forEach((label) => {
                    if (!label.labelUrl) return;

                    if (
                      label.labelType === "QR_CODE" &&
                      submittedReturn.shippingLabelInfoToken
                    ) {
                      actions.push({
                        target: "_blank",
                        to: `/printerless-info?return_id=${submittedReturn.shippingLabelInfoToken}`,
                        langKey: `returnsItemListPending${label.labelType.toLowerCase()}Link`,
                      });
                    } else {
                      actions.push({
                        href: label.labelUrl,
                        langKey: `returnsItemListPending${label.labelType.toLowerCase()}Link`,
                      });
                    }
                  });
                }
                if (submittedReturn.canBeCancelled) {
                  actions.push({
                    langKey: "returnsItemListPendingCancelReturnLink",
                    onClick: () => {
                      if (isBeingCancelled) return;
                      handleCancel(submittedReturn);
                    },
                    status:
                      cancelReturnStatus?.returnId === submittedReturn.id
                        ? cancelReturnStatus
                        : undefined,
                  });
                }
              }
              return (
                <div className={styles.shipmentWrapper} key={i}>
                  <div className={styles.shipmentHeader}>
                    <MediumHeader>
                      {submittedReturn.displayStatus ? (
                        <StyledMarkup content={submittedReturn.displayStatus} />
                      ) : (
                        <Language
                          noWrap
                          langKey="returnsItemListReturnInitiatedTitle"
                          defaultString={returnsItemListReturnInitiatedTitle}
                        />
                      )}
                    </MediumHeader>
                    {showShipmentActions && (
                      <ul className={styles.shipmentActionsWrapper}>
                        {actions.map(
                          (
                            { title, href, langKey, status, to, ...props },
                            j,
                          ) => (
                            <li className={styles.shipmentAction} key={j}>
                              {to ? (
                                <Link to={to || ""} {...props}>
                                  {title ? (
                                    <StyledMarkup noWrap content={title} />
                                  ) : (
                                    <Language noWrap langKey={langKey ?? ""} />
                                  )}
                                </Link>
                              ) : (
                                <TextLink
                                  href={href}
                                  linkStyle={{
                                    sDefault: {
                                      ...linksNoneDefault,
                                      color: bodytextDefaultNone?.color,
                                      ...(status?.status === "loading"
                                        ? {
                                            cursor: "not-allowed",
                                            opacity: 0.5,
                                          }
                                        : undefined),
                                    },
                                    sHover: {
                                      ...linksNoneHover,
                                      color: bodytextDefaultNone?.color,
                                      ...(status?.status === "loading"
                                        ? {
                                            cursor: "not-allowed",
                                            opacity: 0.5,
                                          }
                                        : undefined),
                                    },
                                  }}
                                  onClick={props.onClick}>
                                  {title ? (
                                    <StyledMarkup noWrap content={title} />
                                  ) : (
                                    <Language noWrap langKey={langKey ?? ""} />
                                  )}
                                </TextLink>
                              )}
                              {status?.status === "loading" && (
                                <span className={styles.spinnerContainer}>
                                  <Spinner width={12} />
                                </span>
                              )}
                            </li>
                          ),
                        )}
                      </ul>
                    )}
                  </div>
                  <div className={styles.shipmentItems}>
                    {shipment.items.map((item) => (
                      <ItemCard
                        thumbnail={item.imageUrl}
                        title={item.name}
                        attributes={getItemAttrs({
                          locale,
                          includeCurrency,
                          item,
                          itemDisplay,
                          labels: {
                            skuLabel,
                            qtyLabel,
                            colorLabel,
                            sizeLabel,
                          },
                        })}
                        hasShippingProtection={item.hasShippingProtection}
                        key={`${item.sku ?? ""}${item.itemId ?? ""}`}
                        disabled
                        sDefault={{
                          ...sDefault,
                          backgroundColor:
                            documentbodyNoneNone?.backgroundColor,
                        }}
                        additionalInfo={`${reasonLabel ?? ""}${
                          item.reason?.name
                        }`}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </React.Fragment>
        );
      })}

      {ineligibleItems.length > 0 && (
        <>
          <MediumHeader>
            <Language
              noWrap
              langKey="returnsItemListIneligibleDescription"
              defaultString={returnsItemListIneligibleDescription}
            />
          </MediumHeader>
          {ineligibleItems.map((item) => (
            <ItemCard
              thumbnail={item.imageUrl ?? ""}
              title={item.name}
              attributes={getItemAttrs({
                locale,
                includeCurrency,
                item: {
                  ...item,
                  quantity: item.remainingQuantity,
                },
                itemDisplay,
                labels: {
                  skuLabel,
                  qtyLabel,
                  colorLabel,
                  sizeLabel,
                },
              })}
              hasShippingProtection={item.hasShippingProtection}
              additionalInfo={item.eligibilities.return?.message ?? ""}
              key={`${item.sku ?? ""}${item.itemId ?? ""}`}
              disabled
              sDefault={{
                ...sDefault,
                backgroundColor: documentbodyNoneNone?.backgroundColor,
              }}
            />
          ))}
        </>
      )}

      {((selectedItems && selectedItems.length > 0) || disclaimer) && (
        <StickyFooter>
          <Footer
            onEvent={handleSubmit}
            progressiveStep={progressiveStep}
            activeStep={activeStep}
            above={disclaimer}
            buttons={{
              centerButton: {
                label: submitLabel,
                disabled: !selectedItems?.length,
              },
            }}
          />
        </StickyFooter>
      )}
    </>
  );
};

export const ItemSelectorRaw = withSettingsProvider(
  withTranslationsProvider(
    withThemeProvider(ItemSelectorRawC, {
      themeTokens,
    }),
    { translationKeys },
  ),
  {
    settingsRequested: `
      itemDisplay {
        additionalItemAttributeConfig {
          attribute
          value {
            concatKey
            concatPosition
            label
            transform
          }
        }
        itemAttributeConfig
      }
    `,
  },
);
